import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/apiService';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiUsers, FiX } from 'react-icons/fi';
import { useT } from '../../utils/i18n';

const ADMIN_DAILY_LIMIT = 50;

const EMPTY_USER = { name: '', email: '', password: '', role: 'editor', dailyBudget: 10 };

const getBudgetColor = (pct) => {
  if (pct >= 100) return 'text-red-600 bg-red-50';
  if (pct >= 90)  return 'text-orange-600 bg-orange-50';
  if (pct >= 80)  return 'text-yellow-600 bg-yellow-50';
  return 'text-green-600 bg-green-50';
};

// Shared form fields used by both Create and Edit
const UserForm = ({ data, onChange, isEdit }) => {
  const t = useT();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.fullName')}</label>
        <input
          type="text"
          value={data.name}
          onChange={e => onChange('name', e.target.value)}
          className="input"
          placeholder="John Doe"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.email')}</label>
        <input
          type="email"
          value={data.email}
          onChange={e => onChange('email', e.target.value)}
          className="input"
          placeholder="user@example.com"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isEdit ? t('admin.newPassword') : t('admin.password')}
          {isEdit && <span className="text-gray-400 font-normal ml-1">{t('admin.leaveBlankPassword')}</span>}
        </label>
        <input
          type="password"
          value={data.password}
          onChange={e => onChange('password', e.target.value)}
          className="input"
          placeholder="Min. 6 characters"
          minLength={isEdit ? undefined : 6}
          required={!isEdit}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.role')}</label>
        <select
          value={data.role}
          onChange={e => onChange('role', e.target.value)}
          className="input"
        >
          <option value="editor">{t('admin.editor')}</option>
          <option value="manager">{t('admin.manager')}</option>
          <option value="admin">{t('admin.admin')}</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('admin.dailyBudget')}{' '}
          {data.role === 'admin'
            ? <span className="text-gray-400 font-normal">{t('admin.fixedForAdmins')}</span>
            : <span className="font-bold text-primary-600">${data.dailyBudget}</span>
          }
        </label>
        <input
          type="range"
          min="1"
          max="50"
          value={data.role === 'admin' ? 50 : data.dailyBudget}
          onChange={e => onChange('dailyBudget', Number(e.target.value))}
          className="w-full accent-primary-600"
          disabled={data.role === 'admin'}
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>$1</span><span>$25</span><span>$50</span>
        </div>
      </div>
    </div>
  );
};

export const AdminPanel = () => {
  const t = useT();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ ...EMPTY_USER });

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      setUsers(await adminService.listUsers());
    } catch {
      toast.error(t('admin.failedLoadUsers'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await adminService.createUser(newUser);
      toast.success(t('admin.userCreated', { email: newUser.email }));
      setShowCreateForm(false);
      setNewUser({ ...EMPTY_USER });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || t('admin.failedCreateUser'));
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editingUser };
      if (!payload.password) delete payload.password;
      await adminService.updateUser(editingUser._id, payload);
      toast.success(t('admin.userUpdated'));
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || t('admin.failedUpdateUser'));
    }
  };

  const openEdit = (user) => {
    setEditingUser({ ...user, password: '' });
    setShowCreateForm(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FiUsers size={24} className="text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.userManagement')}</h1>
        </div>
        <button onClick={() => { setShowCreateForm(v => !v); setEditingUser(null); }} className="btn btn-primary">
          <FiPlus size={16} />
          {t('admin.createUser')}
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.newUser')}</h2>
            <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600">
              <FiX size={20} />
            </button>
          </div>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <UserForm data={newUser} onChange={(k, v) => setNewUser(p => ({ ...p, [k]: v }))} isEdit={false} />
            <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowCreateForm(false)} className="btn btn-ghost">{t('admin.cancel')}</button>
              <button type="submit" className="btn btn-primary"><FiPlus size={16} />{t('admin.createUser')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('admin.editUser')} {editingUser.name}</h2>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600">
                <FiX size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSave} className="p-6 space-y-4">
              <UserForm
                data={editingUser}
                onChange={(k, v) => setEditingUser(p => ({ ...p, [k]: v }))}
                isEdit={true}
              />
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setEditingUser(null)} className="btn btn-ghost">{t('admin.cancel')}</button>
                <button type="submit" className="btn btn-primary">{t('admin.saveChanges')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('admin.loadingUsers')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.user')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.role')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.todaySpend')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.dailyLimit')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.remaining')}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(user => {
                const limit = user.role === 'admin' ? ADMIN_DAILY_LIMIT : (user.dailyLimit ?? user.dailyBudget ?? 10);
                const pct = limit > 0 ? Math.min(100, (user.todaySpend / limit) * 100) : 0;
                return (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-gray-500 text-xs">{user.email}</p>
                      {user.organization && <p className="text-gray-400 text-xs">{user.organization}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${user.role === 'admin' ? 'badge-primary' : 'badge-warning'} text-xs`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getBudgetColor(pct)}`}>
                        ${user.todaySpend.toFixed(4)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {user.role === 'admin'
                        ? <span className="text-gray-400 text-xs">${ADMIN_DAILY_LIMIT} ({t('admin.fixed')})</span>
                        : <span>${limit}</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">${(user.remainingBudget ?? 0).toFixed(2)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openEdit(user)}
                        className="btn btn-ghost btn-sm"
                        title={t('admin.edit')}
                      >
                        <FiEdit2 size={14} />
                        {t('admin.edit')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">{t('admin.noUsersFound')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
